import { Flatfile } from '@flatfile/api';
import {
  VISITOR_CUSTOM_FIELD_TYPE_VALUES,
  isVisitorCustomFieldSelectType,
  type VisitorCustomFieldType,
} from 'constants-visitor-management';

/** Flatfile column key is `fieldKey`; API payloads need `customFieldConfigId`. */
export type FormCustomFieldColumn = {
  /** Config UUID; required for persisting CSV values via createInteraction (same shape as single invite). */
  fieldKey: string;
  fieldName: string;
  customFieldConfigId: string;
  fieldType?: string;
  options?: {
    value: string;
    displayText: string;
  }[]
};

export const BASE_FIELD_KEYS = new Set([
  'email',
  'firstName',
  'lastName',
  'countryCode',
  'cellNumber',
  'company',
]);

const BASE_FIELDS: Flatfile.Property[] = [
  {
    key: 'email',
    type: 'string',
    label: 'Visitor Email',
    constraints: [{ type: 'unique' }],
  },
  {
    key: 'firstName',
    type: 'string',
    label: 'Visitor First Name',
    constraints: [{ type: 'required' }],
  },
  {
    key: 'lastName',
    type: 'string',
    label: 'Visitor Last Name',
    constraints: [{ type: 'required' }],
  },
  {
    key: 'countryCode',
    type: 'string',
    label: 'Visitor Country Code',
  },
  {
    key: 'cellNumber',
    type: 'string',
    label: 'Visitor Cell Number',
  },
  {
    key: 'company',
    type: 'string',
    label: 'Visitor Company',
  },
];

function buildCustomFieldProperty({
  fieldKey,
  fieldName,
  fieldType,
  options,
}: FormCustomFieldColumn): Flatfile.Property {
  const isSelectType = fieldType
    ? isVisitorCustomFieldSelectType(fieldType as VisitorCustomFieldType)
    : false;
  const isMultiSelect = fieldType === VISITOR_CUSTOM_FIELD_TYPE_VALUES.MULTI_SELECT;
  const isSingleSelect = isSelectType && !isMultiSelect;

  if ((isSingleSelect || isMultiSelect) && options?.length) {
    const config = {
      allowCustom: false,
      options: options.map(option => ({
        value: option.value,
        label: option.displayText,
      })),
    };

    // `enum-list` is the picklist type Flatfile renders as a multi-value (checkbox) picker.
    // `enum` (even with `multi: true`) still renders as a single-value picker in the sheet grid.
    return isMultiSelect
      ? {
          key: fieldKey,
          type: 'enum-list',
          label: fieldName,
          constraints: [{ type: 'required' }],
          config,
        }
      : {
          key: fieldKey,
          type: 'enum',
          label: fieldName,
          constraints: [{ type: 'required' }],
          config,
        };
  }

  return {
    key: fieldKey,
    type: 'string',
    label: fieldName,
    constraints: [{ type: 'required' }],
  };
}

/**
 * Builds Flatfile workbook config with base visitor fields plus form custom fields.
 * Custom fields added to the registration form appear as required columns so uploads align with form config.
 * Custom fields whose `fieldKey` matches a base column key are omitted to avoid duplicate Flatfile keys and
 * misrouting in `processVisitorData`.
 * Choice-based fields render as a picklist when options are configured: `select_dropdown` and
 * `select_radio` as a single-value `enum`, `multi_select` as a multi-value `enum-list`. Otherwise
 * they fall back to a plain string column.
 */
export function buildWorkbookConfig(
  formCustomFields: FormCustomFieldColumn[] = []
): Flatfile.CreateWorkbookConfig {
  const customFieldProperties: Flatfile.Property[] = formCustomFields
    .filter(({ fieldKey }) => !BASE_FIELD_KEYS.has(fieldKey))
    .map(buildCustomFieldProperty);

  return {
    name: 'Import Visitors',
    labels: ['pinned'],
    sheets: [
      {
        name: 'Visitors',
        slug: 'visitors',
        fields: [...BASE_FIELDS, ...customFieldProperties],
      },
    ],
    actions: [
      {
        operation: 'submitActionFg',
        mode: 'foreground',
        label: 'Submit',
        description: 'Submit data',
        primary: true,
        constraints: [{ type: 'hasAllValid' }, { type: 'hasData' }],
      },
    ],
  };
}
